import { useState } from 'react';
import { TEMPLATE_BASE64 as excelTemplate_tong_hop_mau_bao_cao_M_u_b_o_c_o_KQSXKD } from '@/lib/templates-base64/MAU-KQSXKD';
import { AritoDialog, AritoForm, AritoIcon, BulkDeleteDialog, ConfirmationDialog } from '@/components/custom/erp';
import { handleImportExcel, ColumnMapping } from '../utils/excel-import';
import { downloadBase64Excel } from '@/lib/template-downloader';
import { getInputTableColumns } from './detail-table/cols';
import { ErrorDialog } from './components/ErrorDialog';
import { ValidationError } from '../utils/validation';
import { Button } from '@/components/ui/button';
import DetailFormDialog from './detail-form';
import DetailTableTab from './detail-table';
import useDetail from '../hooks/useDetail';
import { QUERY_KEYS } from '@/constants';
import { toast, useCRUD } from '@/hooks';

interface DetailDialogProps {
  selectedTemplateUuid: string;
  onClose: () => void;
}

const DetailDialog: React.FC<DetailDialogProps> = ({ selectedTemplateUuid, onClose }) => {
  const [errorData, setErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);
  const { data, refreshData } = useCRUD({
    endpoint: QUERY_KEYS.CHI_TIET_MAU_BAO_CAO_KET_QUA_HOAT_DONG_KINH_DOANH,
    searchData: { id_maubc: selectedTemplateUuid },
    pageSize: 100
  });
  const { addItem, updateItem, deleteItem } = useCRUD({
    endpoint: QUERY_KEYS.CHI_TIET_MAU_BAO_CAO_KET_QUA_HOAT_DONG_KINH_DOANH,
    initialFetch: false,
    pageSize: 100
  });
  const {
    isCopy,
    mode,
    selectedRow,
    showDeleteDialog,
    showFormDetail,
    handleAdd,
    handleCopy,
    handleDelete,
    handleEdit,
    handleRowClick,
    handleView,
    handleCloseForm,
    handleCloseDeleteDialog
  } = useDetail();

  const handleSave = async (data: any) => {
    const templateUuid = selectedTemplateUuid || data?.id_maubc;
    if (!templateUuid) {
      toast({
        description: 'Vui lòng chọn mẫu báo cáo trước khi lưu chi tiết.',
        variant: 'destructive'
      });
      return;
    }

    const payload = {
      ...data,
      id_maubc: templateUuid
    };
    try {
      if (mode === 'add') {
        await addItem(payload);
      } else {
        await updateItem(selectedRow?.uuid, payload);
      }
      await refreshData();
      handleCloseForm();
    } catch (error) {
      return;
    }
  };

  const handleDownloadTemplate = () => {
    downloadBase64Excel(excelTemplate_tong_hop_mau_bao_cao_M_u_b_o_c_o_KQSXKD, 'Mẫu báo cáo KQSXKD.xlsx');
  };

  const onImportExcel = async () => {
    if (!selectedTemplateUuid) {
      toast({
        description: 'Vui lòng chọn mẫu báo cáo trước khi import.',
        variant: 'destructive'
      });
      return;
    }

    await handleImportExcel(
      async importedData => {
        try {
          for (const item of importedData) {
            await addItem({
              ...item,
              id_maubc: selectedTemplateUuid
            });
          }
          await refreshData();
        } catch (error) {
          console.error('Lỗi khi import:', error);
        }
      },
      args => setErrorData(args)
    );
  };
  return (
    <>
      <AritoDialog
        open={true}
        onClose={onClose}
        title='Thông tin báo cáo'
        titleIcon={<AritoIcon icon={12} />}
        zIndex={100}
        maxWidth='xl'
        actions={
          <Button variant='outline' onClick={onClose} className='flex items-center gap-2'>
            <AritoIcon icon={885} />
            Đóng
          </Button>
        }
      >
        <div onSubmit={e => e.stopPropagation()} className='flex h-full min-h-[60vh] w-full flex-col bg-gray-50'>
          <DetailTableTab
            mode='add'
            rows={data || []}
            selectedRowUuid={selectedRow?.uuid}
            onAdd={handleAdd}
            onCopy={handleCopy}
            onDelete={handleDelete}
            onEdit={handleEdit}
            onRowClick={handleRowClick}
            onView={handleView}
            onRefresh={refreshData}
            onDownloadTemplate={handleDownloadTemplate}
            onImportExcel={onImportExcel}
          />
        </div>
      </AritoDialog>

      {showFormDetail && (
        <DetailFormDialog
          open={true}
          onClose={handleCloseForm}
          onSave={handleSave}
          mode={mode}
          initialData={mode === 'add' && !isCopy ? undefined : selectedRow}
        />
      )}

      {showDeleteDialog && (
        <BulkDeleteDialog
          title='Xoá dòng chi tiết'
          onClose={handleCloseDeleteDialog}
          columns={getInputTableColumns()}
          dataRows={data}
          handleDelete={async uuid => {
            await deleteItem(uuid);
          }}
          onComplete={refreshData}
        />
      )}
      {errorData && (
        <ErrorDialog
          open={!!errorData}
          onClose={() => setErrorData(null)}
          errors={errorData.errors}
          columnMapping={errorData.columnMapping}
        />
      )}
    </>
  );
};

export default DetailDialog;
